import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Download, Loader2, RefreshCw } from 'lucide-react';
import { Button } from '../../ui/button';
import { API_BASE } from '../../../config/api';
import { HeadlineCards } from './HeadlineCards';
import { ModuleCompletionChart, AssignmentsChart, WeeklyActiveChart } from './InsightsCharts';
import { UnitScoresTable, LabBadgesTable } from './InsightsTables';
import { PendingLearnersDialog } from './PendingLearnersDialog';

const API = `${API_BASE}/api`;

export default function InsightsStep({ programmeId, headers }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [drill, setDrill] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/programmes/${programmeId}/insights`, { headers });
      setData(res.data);
    } catch (e) {
      toast.error('Could not load insights');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [programmeId]);

  useEffect(() => { load(); }, [load]);

  const exportExcel = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/programmes/${programmeId}/insights/export`, { headers, responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(data?.programme_name || 'programme').replace(/[^a-z0-9]+/gi, '_')}_insights.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  if (loading && !data) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-teal-400" /></div>;
  if (!data) return null;

  return (
    <div className="space-y-6" data-testid="programme-insights">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-zinc-500">Admin previews are excluded · Updated {new Date(data.generated_at).toLocaleTimeString()}</p>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load} disabled={loading} className="border-zinc-700 text-zinc-300" data-testid="insights-refresh-btn">
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />Refresh
          </Button>
          <Button size="sm" onClick={exportExcel} disabled={exporting} className="bg-amber-400 hover:bg-amber-300 text-black" data-testid="insights-export-btn">
            {exporting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}Export to Excel
          </Button>
        </div>
      </div>
      <HeadlineCards headline={data.headline} />
      <div className="grid lg:grid-cols-2 gap-6">
        <ModuleCompletionChart modules={data.modules} onSelect={(m) => setDrill({ kind: 'module', id: m.module_id, title: m.title })} />
        <WeeklyActiveChart weeks={data.weekly_active} total={data.headline.learners} />
      </div>
      <AssignmentsChart assignments={data.assignments} onSelect={(a) => setDrill({ kind: 'assignment', id: a.activity_id, title: a.title })} />
      <div className="grid lg:grid-cols-2 gap-6">
        <UnitScoresTable units={data.learning_units} />
        <LabBadgesTable labs={data.labs} />
      </div>
      <PendingLearnersDialog programmeId={programmeId} headers={headers} drill={drill} onClose={() => setDrill(null)} />
    </div>
  );
}
